import React, { Component, type ReactNode } from 'react';
import { isChunkLoadError, reloadOnceForStaleChunk } from '@/utils/chunkReload';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorCount: number;
}

// Track errors across all instances to prevent infinite recovery loops
let globalErrorCount = 0;
let lastErrorTime = 0;

// Time window (ms) to count errors
const ERROR_WINDOW_MS = 1000;
// Max errors allowed in window before stopping recovery attempts
const MAX_ERRORS_IN_WINDOW = 3;

class AppErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, errorCount: 0 };
  }

  static getDerivedStateFromError(error: Error): State {
    const now = Date.now();

    // Reset counter if outside time window
    if (now - lastErrorTime > ERROR_WINDOW_MS) {
      globalErrorCount = 0;
    }

    globalErrorCount++;
    lastErrorTime = now;

    // If too many errors in a short time, don't try to recover
    // This prevents infinite loops from React DOM reconciliation errors
    if (globalErrorCount > MAX_ERRORS_IN_WINDOW) {
       
      if (typeof window !== 'undefined') {
        console.error('[AppErrorBoundary] Too many errors in quick succession, stopping recovery to prevent infinite loop');
      }
    }

    return { hasError: true, error, errorCount: globalErrorCount };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    if (isChunkLoadError(error)) {
      reloadOnceForStaleChunk(`ErrorBoundary: ${error.message}`);
      return;
    }
    // Only log the first few errors to prevent console flooding
    if (globalErrorCount <= MAX_ERRORS_IN_WINDOW) {
      console.error('App Error Boundary caught an error:', error);
      console.error('Error Info:', errorInfo);
    }
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center h-screen bg-gray-50 dark:bg-gray-900">
          <div className="text-center max-w-lg mx-auto p-8">
            <div className="mb-6">
              <svg className="w-20 h-20 text-red-500 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
              Application Error
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              An unexpected error occurred. Please refresh the page to continue.
            </p>
            <button
              type="button"
              onClick={() => { window.location.reload() }}
              className="px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
            >
              Refresh Page
            </button>
            {import.meta.env.DEV && this.state.error && (
              <details className="mt-6 text-left">
                <summary className="cursor-pointer text-sm text-gray-500 dark:text-gray-400">
                  Error Details (Development Only)
                </summary>
                <pre className="mt-2 text-xs bg-gray-100 dark:bg-gray-800 p-3 rounded overflow-auto max-h-48 text-red-600 dark:text-red-400">
                  {this.state.error.stack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default AppErrorBoundary;
